import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';
import styles from './SirenNumber.module.scss'

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import Input from '@src/components/_shared/UI/Input';
import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useQuery } from '@tanstack/react-query';
import { getCompanyData } from '@src/api/Referencial';
import { useEffect, useState } from 'react';
import { sirenSchema } from '@src/utilities/schemas';
import { AddressEnumType } from '@src/utilities/types';


export default function SirenNumber() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();

    const [sirenNumber, setSirenNumber] = useState<string>('');
    const [isSubmitAttempted, setIsSubmitAttempted] = useState<boolean>(false);

    const { data, refetch, isError, isLoading, isSuccess } = useQuery({ 
        queryKey: [`companyData`, sirenNumber],
        queryFn: () => getCompanyData({sirenNumber: sirenNumber}),
        enabled: false
    });

    useEffect(() => {
        if (data && isSuccess) {
            const companyFields = {
                'company.basic.name': data.name,
                'company.basic.created_at': data.created_at,
                'company.basic.siret': data.siret,
                'company.basic.rcs': data.siren,
                'company.basic.ape': data.activity_code,
            };
            Object.entries(companyFields).forEach(([key, value]) => {
                setField(key, value);
            });

            const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
            const result = (data.street_number || '').trim().match(regex);
            const number = result && result[1] ? result[1] : '';
            const extension = result && result[2] ? result[2] : '';
            registration.company_addresses.filter(obj => obj.address_type === AddressEnumType.postale).forEach((obj) => {
                obj.street_number = number || data.street_number;
                obj.street_extension = extension.charAt(0).toUpperCase() || '';
                obj.street_name_further = data.street_name_further || '';
                obj.street_name = data.street_type + ' ' + data.street_name;
                obj.city.zipcode = data.postal_code;
                obj.city.name = data.city;
                obj.country = 'FR';
            });
            registration.company_addresses.filter(obj => obj.address_type === AddressEnumType.fiscale).forEach((obj) => {
                obj.street_number = number || data.street_number;
                obj.street_extension = extension.charAt(0).toUpperCase() || '';
                obj.street_name_further = data.street_name_further || '';
                obj.street_name = data.street_type + ' ' + data.street_name;
                obj.city.zipcode = data.postal_code;
                obj.city.name = data.city;
                obj.country = 'FR';
            });

            setBlock('CompanyInformations');
        }
    }, [data, isSuccess]);

    useEffect(() => {
        if (sirenNumber) {
            refetch();
        }
    }, [sirenNumber])

    const _onGoBack = () => {
        setSection(3)
        setBlock('CompanyMisc')
    };

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Registre du commerce et des sociétés" />
            <Subtitle content="Veuillez indiquer le numéro Siren de la société" />
            <Formik
                initialValues={{
                    siren: registration.company.basic.rcs,
                }}
                onSubmit={(values) => {
                    if (registration.company.company_id && registration.company.basic.rcs === values.siren) {
                        setBlock('CompanyInformations')
                    } else {
                        setSirenNumber(values.siren);
                    }
                }}
                validationSchema={sirenSchema}
            >
                {({ handleChange, handleSubmit, values, errors }) => {
                    const clearInput = () => {
                        handleChange('siren')('')
                        setSirenNumber('');
                        setIsSubmitAttempted(false);
                    };
                    const handleValidateClick = () => {
                        setIsSubmitAttempted(true);
                        handleSubmit();
                    };
                    return (
                        <Form id="SirenNumberForm" autoComplete="off" className="registerForm">
                            <Input
                                title="Numéro Siren"
                                name="siren_number"
                                id="siren_number"
                                width="100%"
                                type="number"
                                placeholder="ex : 987654321"
                                value={values.siren}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange('siren')(e.target.value);
                                    if (!e.target.value) {
                                        setIsSubmitAttempted(false);
                                    }
                                }}
                                clearInput={clearInput}
                                hasError={isSubmitAttempted && !!(isError || errors.siren)}
                                errorMessage={errors.siren}
                            />
                            {isError ?
                                <>
                                    <div className={styles.existingErrorContainer}>
                                        <p className={styles.existingError}>Le numéro Siren que vous avez renseigné n’est pas valide ou n’est pas encore enregistré.</p>
                                    </div>
                                </>
                                : null}
                            {isError ?
                                <span className={styles.link} onClick={() => setBlock('CompanyInformations')}>Continuer vers la saisie manuelle</span>
                                : null}
                            <ValidateButton title="Continuer" disabled={isError || !values.siren} onLoading={isLoading} onClick={handleValidateClick} />
                        </Form>
                    )
                }}
            </Formik>
        </Container>
    );
}
